import Decimal from 'decimal.js';
import { cloneDeep, pickBy } from 'lodash-es';
import qs from 'qs';

import type { SortDirection } from 'store/client/graphql';

const STRING_URL_PARAMS = new Set([
  'after',
  // Graphql relay spec needs these to be strings
  'before',
  'keyword',
]);

enum DeeplinkUrlParameter {
  COLUMNS = 'columns',
  NESTED_VIEW = 'nestedView',
}

export const TABLE_COLUMNS_URL_PARAM = 'columns';

/**
 * Search params that belong to the detail view, not the list view.
 * Excluded from list-level concerns like pagination reset, scroll reset, and item URL generation.
 */
export const DETAIL_ONLY_PARAMS = ['websiteId'] as const;

/**
 * Query-string key carrying the post-login return path (see {@link buildContinueSearch}). Named
 * `continue` after the widely-used convention (e.g. Google), which reads as "continue to where you
 * were headed" — describing intent, not value shape. Unlike that usage we only ever carry an
 * app-relative path here, never an absolute URL (enforced by {@link getSafeContinuePath}).
 */
export const CONTINUE_PARAM = 'continue';

/**
 * Build the `continue=<encoded>` query fragment for a login redirect, so a user bounced to
 * `/login` is returned to `target` after authenticating. `target` is an app-relative path
 * (pathname + search + hash). Encoding is handled by `URLSearchParams` (no base64).
 *
 * @param target - The app-relative path to return to after login.
 * @returns The query string (without leading `?`), e.g. `continue=%2Fretail%2F123`.
 */
export const buildContinueSearch = (target: string): string =>
  new URLSearchParams({ [CONTINUE_PARAM]: target }).toString();

/**
 * Read a safe post-login return path from a location search string. Returns the app-relative
 * path only when it is same-origin — guarding against open redirects by rejecting absolute URLs and
 * protocol-relative (`//host`) values — otherwise undefined.
 *
 * @param search - The location search string (e.g. `window.location.search`).
 * @returns The validated app-relative path, or undefined when absent or unsafe.
 */
export const getSafeContinuePath = (search: string): string | undefined => {
  const raw = new URLSearchParams(search).get(CONTINUE_PARAM);

  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return undefined;
  }

  try {
    const url = new URL(raw, globalThis.location.origin);

    if (url.origin !== globalThis.location.origin) {
      return undefined;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
};

export const getUrlDeeplinkParams = (urlParamString: string): Record<DeeplinkUrlParameter, any> => {
  const availableParameters = Object.values(DeeplinkUrlParameter);
  const parameters = qs.parse(urlParamString, { comma: true, ignoreQueryPrefix: true });

  return pickBy(parameters, (value, key) => availableParameters.includes(key as DeeplinkUrlParameter)) as Record<
    DeeplinkUrlParameter,
    string
  >;
};

/**
 * Given a url string, this util will parse the url and if the column param is found, will return an array of column
 * values.
 * @example /retail/table?columns=rooftop,status,type => ['rooftop', 'status', 'type']
 * @example /retail/table?columns=status => ['status']
 */
export const getTableColumnParams = (urlParameterString: string): string[] => {
  const parsed = qs.parse(urlParameterString, { comma: true, ignoreQueryPrefix: true });
  const columns = parsed?.columns;

  if (!columns) {
    return [];
  }

  // Qs.parse returns a string for single values and an array for multiple values
  return Array.isArray(columns) ? columns : [columns];
};

type DecodeSearchParamOptions = {
  coerceNumbers: boolean;
};

/**
 * Decodes a single url search param value into a primitive, since qs's default parser only ever returns strings.
 *
 * @param value - the raw, still-encoded param value
 * @param charset - the charset qs detected for the query string
 * @param coerceNumbers - whether numeric-looking text becomes a number. Disabled for {@link STRING_URL_PARAMS},
 * whose text has to survive verbatim: coercion drops leading zeros and trailing decimal zeros, and `areParamsEqual`
 * compares a stringified param set against the url, so a value that cannot round trip never compares equal and the
 * effect syncing search params to the url pushes history forever.
 * @source https://github.com/ljharb/qs/issues/91#issuecomment-437926409
 * @example Price filter range keys e.g. `gte` and `lte` should be `Int` not `String`.
 */
const decodeSearchParamValue = (value: string, charset: string, { coerceNumbers }: DecodeSearchParamOptions) => {
  const stringWithoutPlus = value.replaceAll('+', ' ');
  if (charset === 'iso-8859-1') {
    return stringWithoutPlus.replaceAll(/%[\da-f]{2}/gi, unescape);
  }

  if (coerceNumbers && /^(\d+|\d*\.\d+)$/.test(value)) {
    return Number.parseFloat(value);
  }

  const keywords = {
    false: false,
    null: null,
    true: true,
    undefined,
  };
  if (value in keywords) {
    return keywords[value];
  }

  try {
    return decodeURIComponent(stringWithoutPlus);
  } catch {
    return stringWithoutPlus;
  }
};

/**
 * Given a url string, this util will parse the url string and return an object where the key/value pair is to be
 * used in searching/filtering. Ex '?year=2012&make=test' will return { year: 2012, make: 'test' }, which can be
 * used to filter items by those conditions. Any url params that are used for deeplinking
 * purposes (see DeeplinkUrlParams) will be ignored as those are not meant to be included in any searching/filtering
 *
 * @param location - the url search string (comes from the router)
 */
export const getUrlSearchParams = (location: { search: string }): Record<string, SerializableValue> => {
  const parseOptions = {
    comma: true,
    ignoreQueryPrefix: true,
    strictNullHandling: true,
  };

  const parsed = qs.parse(location.search, {
    ...parseOptions,
    decoder: (string_, decoder, charset) => decodeSearchParamValue(string_, charset, { coerceNumbers: true }),
  });

  /*
   * STRING_URL_PARAMS are read from this second, coercion-free pass so their url text survives verbatim. qs hands
   * the decoder a value without its key, so a single parse cannot decide whether to coerce on a per-param basis.
   */
  const textParsed = qs.parse(location.search, {
    ...parseOptions,
    decoder: (string_, decoder, charset) => decodeSearchParamValue(string_, charset, { coerceNumbers: false }),
  });

  const searchParameters = pickBy(
    parsed,
    (value, key) =>
      !Object.values(DeeplinkUrlParameter).includes(key as DeeplinkUrlParameter) && key !== TABLE_COLUMNS_URL_PARAM
  );

  const formattedSearchParameters: Record<string, SerializableValue> = cloneDeep(searchParameters) as Record<
    string,
    SerializableValue
  >;

  for (const key of Object.keys(searchParameters)) {
    if (STRING_URL_PARAMS.has(key)) {
      formattedSearchParameters[key] = String(textParsed[key]);
    }
  }

  return formattedSearchParameters;
};

type SerializableValue =
  boolean | Decimal | null | number | SerializableValue[] | string | undefined | { [key: string]: SerializableValue };

type SerializedValue =
  boolean | null | number | SerializedValue[] | string | undefined | { [key: string]: SerializedValue };

/**
 * Convert Decimal objects to strings recursively to avoid circular reference errors in qs.stringify
 */
const serializeDecimals = (object: SerializableValue): SerializedValue => {
  if (Decimal.isDecimal(object)) {
    return object.toString();
  }

  if (Array.isArray(object)) {
    return object.map(item => serializeDecimals(item));
  }

  if (object && typeof object === 'object') {
    const serialized: { [key: string]: SerializedValue } = {};

    for (const [key, value] of Object.entries(object)) {
      serialized[key] = serializeDecimals(value);
    }

    return serialized;
  }

  return object;
};

export const stringifyParams = (parameters: Record<string, SerializableValue>): string => {
  const serializedParameters = serializeDecimals(parameters);

  return qs.stringify(serializedParameters, { encode: true, strictNullHandling: true });
};

export const areParamsEqual = (
  searchParameters: Record<string, SerializableValue>,
  location: { search: string }
): boolean => stringifyParams(searchParameters) === stringifyParams(getUrlSearchParams(location));

/**
 * Dynamically prepend 'https://' to a given URL string if the url does not already have 'https' or 'http'.
 * @param url - the url to prepend the protocol to
 * @param protocol - the protocol type to add, can be https or http (https is the default)
 */
export const includeUrlProtocol = (url: null | string | undefined, protocol: 'http' | 'https' = 'https') => {
  if (!url) {
    return null;
  }

  return !url.toLowerCase().includes('http://') && !url.toLowerCase().includes('https://')
    ? `${protocol}://${url}`
    : url;
};

/**
 * Util for generating a URL path for given set of list configurations (filters, sort order, keyword search, etc.)
 *
 * @param basePath: The base path for the url (ex /user)
 * @param filters: Filters are a generic object with key/value pairing. Ex { name: 'test' } will be name=test in the URL
 * @param sort: The sort order of the list.
 * @param keyword: Any active keyword searches present in the list
 */
export const generateUrlPathFromListFilters = (
  basePath: string,
  filters: Record<string, unknown>,
  sort?: SortDirection,
  keyword?: string
): string => {
  const parameters = stringifyParams({ ...filters, keyword, sort });

  return `${basePath}?${parameters}`;
};

/**
 * Opens a new tab with the open() method, maintains the Session Context
 *
 * @example If logged in as-user context, will not lose as-user context if open on a new tab.
 *
 * TODO: https://einc.atlassian.net/browse/ED-6743
 *
 */
export const openNewTab = (url: string) => window.open(url, '_blank');

export const getMetricDeeplinkUrl = (columns: string[], separator?: string): string => {
  const results = columns.join(separator);

  return results;
};

/**
 * Extracts the first section from a given pathname.
 *
 * This function takes a string representing a URL pathname and returns the first section of the pathname.
 * Sections are defined as strings separated by forward slashes ("/").
 *
 * @param {string} pathname - The URL pathname to extract the section from.
 * @returns {string} The first section of the URL pathname, prepended with a forward slash ("/").
 *
 * @example
 * // Returns "/"
 * getSectionPath("/");
 *
 * @example
 * // Returns "/retail"
 * getSectionPath("/retail");
 *
 * @example
 * // Returns "/retail"
 * getSectionPath("/retail/zAb918azA_g");
 *
 * @example
 * // Returns "/retail"
 * getSectionPath("/retail/table");
 *
 * @example
 * // Returns "/retail"
 * getSectionPath("/retail/table/zAb918azA_g");
 */
export const getSectionPath = (pathname: string): string => {
  const section = pathname.split('/').find(Boolean) || '';

  return `/${section}`;
};

/**
 * Strip protocol (http/https) and www. prefix from URL for display purposes.
 *
 * @param url - The URL to strip
 * @returns The URL without protocol and www. prefix
 *
 * @example
 * stripUrlPrefix('https://www.example.com') // 'example.com'
 * stripUrlPrefix('https://example.com') // 'example.com'
 * stripUrlPrefix('http://www.example.com') // 'example.com'
 * stripUrlPrefix('https://dealer.example.com') // 'dealer.example.com'
 * stripUrlPrefix('www.example.com') // 'example.com'
 */
export const stripUrlPrefix = (url: string): string => url.replace(/^(https?:\/\/)?(www\.)?/, '');

/**
 * Extracts the YouTube video ID from a variety of YouTube URL formats.
 *
 * Supported formats: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID,
 * youtube.com/shorts/ID, youtube.com/v/ID.
 *
 * @param url - A YouTube URL (or any string).
 * @returns The 11-character video ID if the URL is a recognized YouTube link, otherwise null.
 */
export const getYouTubeVideoId = (url: null | string | undefined): null | string => {
  if (!url) {
    return null;
  }

  const match = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );

  return match?.[1] ?? null;
};

/**
 * YouTube thumbnail qualities, ordered from smallest to largest. YouTube only serves
 * thumbnails at these fixed sizes; there is no way to request arbitrary dimensions.
 */
const YOUTUBE_THUMBNAIL_SIZES = [
  { height: 90, name: 'default', width: 120 },
  { height: 180, name: 'mqdefault', width: 320 },
  { height: 360, name: 'hqdefault', width: 480 },
  { height: 480, name: 'sddefault', width: 640 },
  { height: 720, name: 'maxresdefault', width: 1280 },
] as const;

type YouTubeThumbnailSize = {
  /**
   * Target height in pixels. The smallest thumbnail that covers both width and height wins.
   */
  height?: number;
  /**
   * Target width in pixels. The smallest thumbnail that covers both width and height wins.
   */
  width?: number;
};

/**
 * Builds a YouTube thumbnail URL for the given video URL, or null if the video ID cannot be
 * extracted.
 *
 * YouTube only hosts thumbnails at five fixed sizes; when `size` is passed the smallest
 * available thumbnail that covers both dimensions is used. Defaults to `hqdefault` (480x360)
 * when no size is specified.
 *
 * @param url - A YouTube URL.
 * @param size - Optional target dimensions; the smallest thumbnail that covers both is returned.
 * @returns The thumbnail image URL, or null if the URL is not a recognized YouTube link.
 */
export const getYouTubeThumbnailUrl = (url: null | string | undefined, size?: YouTubeThumbnailSize): null | string => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) {
    return null;
  }

  if (!size) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }

  const match =
    YOUTUBE_THUMBNAIL_SIZES.find(s => s.width >= (size.width ?? 0) && s.height >= (size.height ?? 0)) ??
    YOUTUBE_THUMBNAIL_SIZES.at(-1);

  return `https://img.youtube.com/vi/${videoId}/${match?.name ?? 'hqdefault'}.jpg`;
};

type YouTubeEmbedOptions = {
  /**
   * Whether to autoplay the video when the embed loads
   */
  autoplay?: boolean;
  /**
   * Whether to mute the video on load
   */
  mute?: boolean;
};

/**
 * Builds a YouTube embed URL for the given video URL, or null if the video ID cannot
 * be extracted. Use as the `src` of an `<iframe>` to render the video inline.
 *
 * @param url - A YouTube URL.
 * @param options - Optional playback options appended as query params.
 * @returns The embed URL (with any options appended), or null if the URL is not a recognized YouTube link.
 */
export const getYouTubeEmbedUrl = (url: null | string | undefined, options?: YouTubeEmbedOptions): null | string => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) {
    return null;
  }
  const base = `https://www.youtube.com/embed/${videoId}`;
  if (!options) {
    return base;
  }
  const parameters = new URLSearchParams();
  if (options.autoplay !== undefined) {
    parameters.set('autoplay', options.autoplay ? '1' : '0');
  }
  if (options.mute !== undefined) {
    parameters.set('mute', options.mute ? '1' : '0');
  }

  return parameters.size > 0 ? `${base}?${parameters.toString()}` : base;
};
